import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { ApiSearchLegacy } from 'src/app/api/services/api-utils.service';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { CompanyStateService } from './company-state.service';
import { UserStateService } from './user-state.service';
import {
  AccessLevel,
  AccessRequirement,
  DISPLAY_ACCESS,
  DisplayAccess,
  getAccessLevelRank,
  getEntityType,
  getFolderAccessLevel,
  meetsAccessLevel,
  PRESENTATION_ACCESS,
  PresentationAccess,
  SCHEDULE_ACCESS,
  ScheduleAccess,
  shareLabelToAccessLevel,
} from '../types/access-level';
import { ENTITY_TYPES, EntityType } from '../types/entity-type';

type EntityAccessLevelByType = {
  [ENTITY_TYPES.PRESENTATION]: PresentationAccess;
  [ENTITY_TYPES.SCHEDULE]: ScheduleAccess;
  [ENTITY_TYPES.DISPLAY]: DisplayAccess;
};

type RoleEntityAccessMap = {
  [K in EntityType]?: EntityAccessLevelByType[K];
};

type AccessControlList = Record<string, string>;

type SharedListItem = {
  id?: string;
  accessControlList?: AccessControlList | null;
  isFolder?: boolean;
};

const DISPLAY_LISTED_MINIMUM_ACCESS = DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR;

const ROLE_ENTITY_ACCESS: Record<string, RoleEntityAccessMap> = {
  sa: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
  },
  ua: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.FULL_ACCESS,
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
  },
  ce: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.EDITOR,
  },
  cp: {
    [ENTITY_TYPES.PRESENTATION]: PRESENTATION_ACCESS.PUBLISHER,
    [ENTITY_TYPES.SCHEDULE]: SCHEDULE_ACCESS.FULL_ACCESS,
  },
  da: {
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.FULL_ACCESS,
  },
  sm: {
    [ENTITY_TYPES.DISPLAY]: DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR,
  },
};

const ROLE_CODES = Object.keys(ROLE_ENTITY_ACCESS);
const SHARED_LIST_SEARCH: ApiSearchLegacy = { sortBy: 'name', count: 1 };
const SCREEN_SHARING_DISPLAY_FILTER = 'licensed:=1&&screenSharingEnabled:=true';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  readonly shareAccessChanged$ = new Subject<void>();

  private shareCache = new Map<string, AccessLevel | null>();
  private pendingShares = new Map<string, Promise<AccessLevel | null>>();

  constructor(
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    private presentationApiService: PresentationApiService,
    private scheduleApiService: ScheduleApiService,
    private displayApiService: DisplayApiService,
    broadcasterService: BroadcasterService,
  ) {
    broadcasterService.on('risevision.company.selectedCompanyChanged', () => {
      this.clearShareCache();
    });
  }

  getRoleAccessLevel(
    entityType: typeof ENTITY_TYPES.PRESENTATION,
  ): PresentationAccess | null;
  getRoleAccessLevel(
    entityType: typeof ENTITY_TYPES.SCHEDULE,
  ): ScheduleAccess | null;
  getRoleAccessLevel(
    entityType: typeof ENTITY_TYPES.DISPLAY,
  ): DisplayAccess | null;
  getRoleAccessLevel(entityType: EntityType): AccessLevel | null {
    return this.resolveRoleAccessLevel(entityType);
  }

  areFoldersSupported(
    kind: EntityType | 'Presentation' | 'Schedule' | 'Display',
  ): boolean {
    if (this.canBrowseAllLibraries()) {
      return true;
    }

    return this.hasAccess(getFolderAccessLevel(kind));
  }

  isSaProductionReleaseEnabled(): boolean {
    return (
      this.userStateService.hasPermission('!PROD') ||
      this.userStateService.hasRole('sa')
    );
  }

  hasAccess(requiredLevel: AccessLevel, entityId?: string): boolean {
    const entityType = getEntityType(requiredLevel);

    if (entityType === null) {
      return false;
    }

    const roleLevel = this.resolveRoleAccessLevel(entityType);
    const shareLevel = entityId
      ? this.getCachedShareAccess(entityType, entityId)
      : null;
    const actualLevel = this.highestAccessLevel(roleLevel, shareLevel);

    if (actualLevel === null) {
      return false;
    }

    return meetsAccessLevel(actualLevel, requiredLevel);
  }

  meetsAccessRequirement(requirement: AccessRequirement = {}): boolean {
    const {
      requireAccess,
      requireRole,
      allowAssignedPublisher,
      allowSharedAccess,
      entityId,
    } = requirement;

    if (
      requireRole &&
      this.isEnvironmentOnlyRequirement(requireRole) &&
      !this.userStateService.hasPermission(requireRole)
    ) {
      return false;
    }

    if (this.userStateService.hasRole('sa')) {
      return true;
    }

    if (this.userStateService.hasRole('ua')) {
      return !this.isSaOnlyRequirement(requirement);
    }

    if (!requireAccess) {
      return this.userStateService.hasPermission(requireRole ?? '');
    }

    const accessGranted =
      this.hasAccess(requireAccess, entityId) ||
      (!!allowSharedAccess &&
        !entityId &&
        this.hasCachedSharedAccess(requireAccess)) ||
      (!!allowAssignedPublisher && this.userStateService.isAssignedPublisher());

    if (!requireRole) {
      return accessGranted;
    }

    const roleGranted = this.userStateService.hasPermission(requireRole);

    if (this.isEnvironmentOnlyRequirement(requireRole)) {
      return accessGranted && roleGranted;
    }

    return accessGranted || roleGranted;
  }

  canBrowseAllLibraries(): boolean {
    return ['sa', 'ua', 'ce', 'cp', 'da'].some((role) =>
      this.userStateService.hasRole(role),
    );
  }

  getHomeState(): string {
    if (this.hasAccess(PRESENTATION_ACCESS.EDITOR)) {
      return 'apps.editor.home';
    }

    if (
      this.hasAccess(DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR) &&
      !this.hasAccess(DISPLAY_ACCESS.FULL_ACCESS) &&
      !this.hasAccess(SCHEDULE_ACCESS.PLAYLIST_MANAGER)
    ) {
      return 'apps.screen-sharing.moderator-join';
    }

    return 'apps.editor.home';
  }

  async canAccessSchedules(): Promise<boolean> {
    if (
      this.canBrowseAllLibraries() ||
      this.hasAccess(SCHEDULE_ACCESS.PLAYLIST_MANAGER)
    ) {
      return true;
    }

    if (this.userStateService.isAssignedPublisher()) {
      const item = await this.listFirstItem(ENTITY_TYPES.SCHEDULE);
      return Boolean(item?.id);
    }

    return this.hasAnySharedAccess(SCHEDULE_ACCESS.PLAYLIST_MANAGER);
  }

  canBrowseDisplays(): boolean {
    if (this.hasAccess(DISPLAY_ACCESS.FULL_ACCESS)) {
      return true;
    }

    return (
      this.userStateService.hasAnyRole() &&
      !this.userStateService.isAssignedPublisher()
    );
  }

  async canAccessDisplays(): Promise<boolean> {
    if (this.canBrowseDisplays()) {
      return true;
    }

    return this.hasAnySharedAccess(DISPLAY_ACCESS.FULL_ACCESS);
  }

  canBrowseScreenSharing(): boolean {
    return this.hasAccess(DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR);
  }

  screenSharingDisplayListFilter(): string {
    if (this.canBrowseScreenSharing()) {
      return SCREEN_SHARING_DISPLAY_FILTER;
    }

    const userId = this.getSharedAccessUserId();

    return userId
      ? `${SCREEN_SHARING_DISPLAY_FILTER}&&accessControlListUsers:=${userId}`
      : `${SCREEN_SHARING_DISPLAY_FILTER}&&accessControlListUsers:=`;
  }

  async canAccessScreenSharing(): Promise<boolean> {
    if (this.canBrowseScreenSharing()) {
      return true;
    }

    return this.hasSharedScreenSharingDisplay();
  }

  async hasAnySharedAccess(requiredLevel: AccessLevel): Promise<boolean> {
    const entityType = getEntityType(requiredLevel);

    if (entityType === null) {
      return false;
    }

    const companyId = this.companyStateService.getSelectedCompanyId();

    try {
      const item = await this.listFirstSharedItem(entityType);

      if (!item?.id || item.isFolder) {
        return false;
      }

      if (this.companyStateService.getSelectedCompanyId() !== companyId) {
        return false;
      }

      const shareLevel = await this.resolveListedItemShareLevel(
        entityType,
        item,
        companyId,
      );

      return Boolean(shareLevel && meetsAccessLevel(shareLevel, requiredLevel));
    } catch {
      return false;
    }
  }

  async loadShareAccess(
    entityType: EntityType,
    entityId: string,
  ): Promise<AccessLevel | null> {
    return this.resolveAndCacheShare(entityType, entityId);
  }

  cacheListedShareAccess(
    entityType: EntityType,
    items: SharedListItem[],
  ): Promise<void> {
    const roleLevel = this.resolveRoleAccessLevel(entityType);
    const fetchMissingAcl =
      roleLevel === null ||
      !meetsAccessLevel(roleLevel, this.fullAccessFor(entityType));

    return Promise.all(
      items
        .filter((item): item is SharedListItem & { id: string } =>
          Boolean(item?.id && !item.isFolder),
        )
        .map((item) =>
          this.resolveAndCacheShare(
            entityType,
            item.id,
            item.accessControlList,
            fetchMissingAcl,
          ),
        ),
    ).then(() => undefined);
  }

  async loadFirstSharedAccess(
    entityType: EntityType,
  ): Promise<AccessLevel | null> {
    const companyId = this.companyStateService.getSelectedCompanyId();

    try {
      const item =
        entityType === ENTITY_TYPES.DISPLAY
          ? await this.listFirstSharedItem(entityType)
          : await this.listFirstItem(entityType);

      if (!item?.id) {
        return null;
      }

      return this.resolveListedItemShareLevel(entityType, item, companyId);
    } catch {
      return null;
    }
  }

  hasCachedSharedAccess(requiredLevel: AccessLevel): boolean {
    const entityType = getEntityType(requiredLevel);

    if (entityType === null) {
      return false;
    }

    const prefix = `${this.companyStateService.getSelectedCompanyId()}:${entityType}:`;

    for (const [key, level] of this.shareCache) {
      if (!key.startsWith(prefix) || !level) {
        continue;
      }

      if (meetsAccessLevel(level, requiredLevel)) {
        return true;
      }
    }

    return false;
  }

  private isSaOnlyRequirement(requirement: AccessRequirement): boolean {
    const requireRole = requirement.requireRole?.trim();

    if (!requireRole) {
      return false;
    }

    const roleTokens = requireRole
      .split(/\s+/)
      .filter((token) => token && !token.startsWith('!'))
      .filter((token) => !this.userStateService.isEnvironmentRole(token));

    return roleTokens.length > 0 && roleTokens.every((role) => role === 'sa');
  }

  private isEnvironmentOnlyRequirement(requireRole: string): boolean {
    const roleTokens = requireRole
      .trim()
      .split(/\s+/)
      .filter((token) => token);

    return (
      roleTokens.length > 0 &&
      roleTokens.every((role) => this.userStateService.isEnvironmentRole(role))
    );
  }

  private resolveRoleAccessLevel(entityType: EntityType): AccessLevel | null {
    let highestRank = 0;
    let highestLevel: AccessLevel | null = null;

    for (const role of ROLE_CODES) {
      if (!this.userStateService.hasRole(role)) {
        continue;
      }

      const level = ROLE_ENTITY_ACCESS[role][entityType];
      if (!level) {
        continue;
      }

      const rank = getAccessLevelRank(level);
      if (rank !== null && rank > highestRank) {
        highestRank = rank;
        highestLevel = level;
      }
    }

    return highestLevel;
  }

  private fullAccessFor(entityType: EntityType): AccessLevel {
    switch (entityType) {
      case ENTITY_TYPES.PRESENTATION:
        return PRESENTATION_ACCESS.FULL_ACCESS;
      case ENTITY_TYPES.SCHEDULE:
        return SCHEDULE_ACCESS.FULL_ACCESS;
      case ENTITY_TYPES.DISPLAY:
        return DISPLAY_ACCESS.FULL_ACCESS;
    }
  }

  private getCachedShareAccess(
    entityType: EntityType,
    entityId: string,
  ): AccessLevel | null {
    return (
      this.shareCache.get(this.shareCacheKey(entityType, entityId)) ?? null
    );
  }

  private async resolveAndCacheShare(
    entityType: EntityType,
    entityId: string,
    accessControlList?: AccessControlList | null,
    fetchMissingAcl = true,
  ): Promise<AccessLevel | null> {
    const cacheKey = this.shareCacheKey(entityType, entityId);
    const listedLevel = accessControlList
      ? this.resolveShareAccessLevel(entityType, accessControlList)
      : null;

    if (listedLevel) {
      this.setCachedShareAccess(cacheKey, listedLevel);
      return listedLevel;
    }

    const pending = this.pendingShares.get(cacheKey);

    if (pending) {
      return pending;
    }

    if (this.shareCache.has(cacheKey)) {
      return this.shareCache.get(cacheKey) ?? null;
    }

    if (!fetchMissingAcl) {
      return null;
    }

    const request = this.fetchAndCacheShare(entityType, entityId, cacheKey);
    this.pendingShares.set(cacheKey, request);

    try {
      return await request;
    } finally {
      this.pendingShares.delete(cacheKey);
    }
  }

  private async fetchAndCacheShare(
    entityType: EntityType,
    entityId: string,
    cacheKey: string,
  ): Promise<AccessLevel | null> {
    try {
      const acl = await this.getAccessControlList(entityType, entityId);
      const shareLevel = this.resolveShareAccessLevel(entityType, acl ?? {});
      this.setCachedShareAccess(cacheKey, shareLevel);
      return shareLevel;
    } catch {
      return this.shareCache.get(cacheKey) ?? null;
    }
  }

  private shareLevelFromListedItem(
    entityType: EntityType,
    item: SharedListItem,
  ): AccessLevel | null {
    const listedLevel = item.accessControlList
      ? this.resolveShareAccessLevel(entityType, item.accessControlList)
      : null;

    if (listedLevel) {
      return listedLevel;
    }

    return entityType === ENTITY_TYPES.DISPLAY
      ? DISPLAY_LISTED_MINIMUM_ACCESS
      : null;
  }

  private resolveShareAccessLevel(
    entityType: EntityType,
    accessControlList: AccessControlList,
  ): AccessLevel | null {
    const label = this.getShareLabel(accessControlList);

    if (!label) {
      return null;
    }

    return shareLabelToAccessLevel(label, entityType);
  }

  private getShareLabel(accessControlList: AccessControlList): string {
    const keys = this.currentUserAclKeys();

    for (const key of keys) {
      const label = accessControlList[key];
      if (label) {
        return label;
      }
    }

    const labelsByLowerKey = new Map<string, string>();
    for (const [aclKey, label] of Object.entries(accessControlList)) {
      if (label) {
        labelsByLowerKey.set(aclKey.toLowerCase(), label);
      }
    }

    for (const key of keys) {
      const label = labelsByLowerKey.get(key.toLowerCase());
      if (label) {
        return label;
      }
    }

    return '';
  }

  private currentUserAclKeys(): string[] {
    const profile = this.userStateService.getCopyOfProfile(true) ?? {};

    return [
      ...new Set(
        [
          this.userStateService.getUsername(),
          this.userStateService.getUserEmail(),
          profile.username,
          profile.id,
          profile.email,
        ].filter(
          (key): key is string => typeof key === 'string' && key.length > 0,
        ),
      ),
    ];
  }

  private highestAccessLevel(
    first: AccessLevel | null,
    second: AccessLevel | null,
  ): AccessLevel | null {
    if (first === null) {
      return second;
    }

    if (second === null) {
      return first;
    }

    const firstRank = getAccessLevelRank(first) ?? 0;
    const secondRank = getAccessLevelRank(second) ?? 0;

    return secondRank > firstRank ? second : first;
  }

  private setCachedShareAccess(
    cacheKey: string,
    level: AccessLevel | null,
  ): void {
    const previous = this.shareCache.has(cacheKey)
      ? this.shareCache.get(cacheKey)
      : undefined;

    this.shareCache.set(cacheKey, level);

    if (previous !== level) {
      this.shareAccessChanged$.next();
    }
  }

  private clearShareCache(): void {
    if (this.shareCache.size === 0 && this.pendingShares.size === 0) {
      return;
    }

    this.shareCache.clear();
    this.pendingShares.clear();
    this.shareAccessChanged$.next();
  }

  private shareCacheKey(entityType: EntityType, entityId: string): string {
    return `${this.companyStateService.getSelectedCompanyId()}:${entityType}:${entityId}`;
  }

  private async resolveListedItemShareLevel(
    entityType: EntityType,
    item: SharedListItem,
    companyId: string,
  ): Promise<AccessLevel | null> {
    if (this.companyStateService.getSelectedCompanyId() !== companyId) {
      return this.shareLevelFromListedItem(entityType, item);
    }

    const shareLevel = await this.resolveAndCacheShare(
      entityType,
      item.id!,
      item.accessControlList,
    );

    if (shareLevel) {
      return shareLevel;
    }

    if (entityType !== ENTITY_TYPES.DISPLAY) {
      return null;
    }

    if (this.companyStateService.getSelectedCompanyId() !== companyId) {
      return DISPLAY_LISTED_MINIMUM_ACCESS;
    }

    this.setCachedShareAccess(
      this.shareCacheKey(entityType, item.id!),
      DISPLAY_LISTED_MINIMUM_ACCESS,
    );
    return DISPLAY_LISTED_MINIMUM_ACCESS;
  }

  private async hasSharedScreenSharingDisplay(): Promise<boolean> {
    if (!this.getSharedAccessUserId()) {
      return false;
    }

    const companyId = this.companyStateService.getSelectedCompanyId();

    try {
      const result = await this.listItems(ENTITY_TYPES.DISPLAY, {
        ...SHARED_LIST_SEARCH,
        filter: this.screenSharingDisplayListFilter(),
      });
      const item = result.items?.[0];

      if (!item?.id || item.isFolder) {
        return false;
      }

      if (this.companyStateService.getSelectedCompanyId() !== companyId) {
        return false;
      }

      const shareLevel = await this.resolveListedItemShareLevel(
        ENTITY_TYPES.DISPLAY,
        item,
        companyId,
      );

      if (this.companyStateService.getSelectedCompanyId() !== companyId) {
        return false;
      }

      return Boolean(
        shareLevel &&
        meetsAccessLevel(shareLevel, DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR),
      );
    } catch {
      return false;
    }
  }

  private async listFirstItem(
    entityType: EntityType,
  ): Promise<SharedListItem | undefined> {
    const result = await this.listItems(entityType, SHARED_LIST_SEARCH);
    return result.items?.[0];
  }

  private async listFirstSharedItem(
    entityType: EntityType,
  ): Promise<SharedListItem | undefined> {
    const search = this.buildSharedItemSearch(entityType);

    if (!search) {
      return undefined;
    }

    const result = await this.listItems(entityType, search);
    return result.items?.[0];
  }

  private buildSharedItemSearch(
    entityType: EntityType,
  ): ApiSearchLegacy | null {
    if (
      entityType !== ENTITY_TYPES.PRESENTATION &&
      entityType !== ENTITY_TYPES.SCHEDULE &&
      entityType !== ENTITY_TYPES.DISPLAY
    ) {
      return SHARED_LIST_SEARCH;
    }

    const userId = this.getSharedAccessUserId();

    if (!userId) {
      return null;
    }

    return {
      ...SHARED_LIST_SEARCH,
      filter: `accessControlListUsers:=${userId}`,
    };
  }

  private getSharedAccessUserId(): string | null {
    const userId = this.userStateService.getCopyOfProfile(true)?.id;

    return typeof userId === 'string' && userId.length > 0 ? userId : null;
  }

  private listItems(
    entityType: EntityType,
    search: ApiSearchLegacy,
    cursor?: string,
  ): Promise<{
    items?: SharedListItem[];
    cursor?: string;
    nextPageToken?: string;
  }> {
    switch (entityType) {
      case ENTITY_TYPES.PRESENTATION:
        return this.presentationApiService.list(search, cursor);
      case ENTITY_TYPES.SCHEDULE:
        return this.scheduleApiService.list(search, cursor);
      case ENTITY_TYPES.DISPLAY:
        return this.displayApiService.list(search, cursor);
      default:
        return Promise.resolve({ items: [] });
    }
  }

  private getAccessControlList(
    entityType: EntityType,
    entityId: string,
  ): Promise<AccessControlList> {
    switch (entityType) {
      case ENTITY_TYPES.PRESENTATION:
        return this.presentationApiService.getAccessControlList(entityId);
      case ENTITY_TYPES.SCHEDULE:
        return this.scheduleApiService.getAccessControlList(entityId);
      case ENTITY_TYPES.DISPLAY:
        return this.displayApiService.getAccessControlList(entityId);
      default:
        return Promise.resolve({});
    }
  }
}
